/**
 * Due "slot" sovrapposti (A/B) a tutto schermo. Ognuno ha un <video> e un <img> con object-fit: contain
 * su sfondo nero. Un solo slot è "visibile" (z-index 2); l'altro sta SOTTO e serve a preparare il
 * contenuto successivo (decodifica già fatta, video caricato e posizionato in pausa).
 *
 * Swap senza nero (`commit`):
 *   1. si avvia il nuovo (play) e si aspetta 'playing' + un frame presentato (requestVideoFrameCallback);
 *   2. SOLO ALLORA lo slot nuovo sale sopra (z-index) -> evento 'show';
 *   3. dopo un paio di frame lo slot vecchio viene messo in pausa e svuotato -> evento 'hide'.
 *   Non esiste uno stato in cui nessuno slot sia visibile.
 *
 * Autoplay: prima si prova con audio; se play() viene rifiutato (NotAllowedError) si ripete muted e lo si
 * registra (`diag().autoplayFallback`). Al primo gesto dell'utente (`userGesture()`) l'audio si riattiva.
 */
import type { EventSink } from '../diag';
import { NULL_SINK } from '../diag';
import type { Size, Snapshot } from '../reporting/screenshot';
import { SupersededError, type PrepareSpec, type Surfaces } from './types';

type SlotId = 'A' | 'B';

interface Slot {
  id: SlotId;
  root: HTMLDivElement;
  video: HTMLVideoElement;
  img: HTMLImageElement;
  url: string | null;
  kind: 'video' | 'image' | null;
}

interface PreparedState {
  slot: Slot;
  spec: PrepareSpec;
  seq: number;
}

type VideoWithRvfc = HTMLVideoElement & {
  requestVideoFrameCallback?: (cb: () => void) => number;
};

export interface SurfacesOptions {
  events?: EventSink;
  log?: (msg: string) => void;
}

const FILL = 'position:absolute;left:0;top:0;width:100%;height:100%;';
const MEDIA_STYLE = `${FILL}object-fit:contain;background:#000;display:none;border:0;`;

const nextFrame = (): Promise<void> => new Promise((r) => {
  const t = setTimeout(r, 100); // rAF non gira in tab nascoste
  requestAnimationFrame(() => { clearTimeout(t); r(); });
});

function waitEvent(el: HTMLMediaElement, names: string[], timeoutMs: number, what: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const done = (fn: () => void) => { cleanup(); fn(); };
    const onOk = () => done(resolve);
    const onErr = () => done(() => reject(new Error(`${what}: errore media (code ${el.error?.code ?? '?'})`)));
    const timer = setTimeout(() => done(() => reject(new Error(`${what}: timeout`))), timeoutMs);
    function cleanup() {
      clearTimeout(timer);
      for (const n of names) el.removeEventListener(n, onOk);
      el.removeEventListener('error', onErr);
    }
    for (const n of names) el.addEventListener(n, onOk);
    el.addEventListener('error', onErr);
  });
}

export class DomSurfaces implements Surfaces {
  private readonly stage: HTMLDivElement;
  private readonly slots: Record<SlotId, Slot>;
  private visible: Slot | null = null;
  private prepared: PreparedState | null = null;
  private seq = 0;
  private muted = false;
  private autoplayFallback = false;
  private endedCb: (() => void) | null = null;
  private errorCb: ((msg: string) => void) | null = null;
  private readonly events: EventSink;
  private readonly log: (msg: string) => void;

  constructor(host: HTMLElement, opts: SurfacesOptions = {}) {
    this.events = opts.events ?? NULL_SINK;
    this.log = opts.log ?? (() => undefined);
    this.stage = document.createElement('div');
    this.stage.style.cssText = `${FILL}background:#000;overflow:hidden;`;
    this.stage.dataset.testid = 'stage';
    host.appendChild(this.stage);
    this.slots = { A: this.makeSlot('A'), B: this.makeSlot('B') };
  }

  private makeSlot(id: SlotId): Slot {
    const root = document.createElement('div');
    root.dataset.slot = id;
    root.style.cssText = `${FILL}background:#000;overflow:hidden;z-index:1;`;
    const video = document.createElement('video');
    video.style.cssText = MEDIA_STYLE;
    video.playsInline = true;
    video.preload = 'auto';
    video.disablePictureInPicture = true;
    const img = document.createElement('img');
    img.style.cssText = MEDIA_STYLE;
    img.decoding = 'async';
    img.alt = '';
    root.append(video, img);
    this.stage.appendChild(root);
    const slot: Slot = { id, root, video, img, url: null, kind: null };
    video.addEventListener('ended', () => { if (this.visible === slot) this.endedCb?.(); });
    video.addEventListener('error', () => {
      if (this.visible === slot) this.errorCb?.(`video ${slot.id}: errore media (code ${video.error?.code ?? '?'})`);
    });
    return slot;
  }

  /** Ruota lo stage per gli schermi in verticale (manifest.device.orientation). */
  setOrientation(deg: 0 | 90 | 180 | 270): void {
    const s = this.stage.style;
    if (deg === 0) {
      s.cssText = `${FILL}background:#000;overflow:hidden;`;
    } else if (deg === 180) {
      s.cssText = `${FILL}background:#000;overflow:hidden;transform:rotate(180deg);`;
    } else {
      s.cssText = 'position:absolute;left:50%;top:50%;width:100vh;height:100vw;background:#000;overflow:hidden;'
        + `transform:translate(-50%,-50%) rotate(${deg}deg);`;
    }
  }

  onEnded(cb: () => void): void { this.endedCb = cb; }
  onError(cb: (msg: string) => void): void { this.errorCb = cb; }

  diag(): { muted: boolean; autoplayFallback: boolean; rvfc: boolean } {
    return { muted: this.muted, autoplayFallback: this.autoplayFallback, rvfc: 'requestVideoFrameCallback' in HTMLVideoElement.prototype };
  }

  /** Primo gesto dell'utente: se l'autoplay con audio era stato rifiutato, riattiva l'audio. */
  userGesture(): void {
    if (!this.autoplayFallback || !this.muted) return;
    this.muted = false;
    if (this.visible) this.visible.video.muted = false;
  }

  visibleSlot(): string | null { return this.visible?.id ?? null; }

  positionMs(): number | null {
    const s = this.visible;
    return s && s.kind === 'video' ? s.video.currentTime * 1000 : null;
  }
  setRate(rate: number): void {
    if (this.visible?.kind === 'video') this.visible.video.playbackRate = rate;
  }
  seekMs(ms: number): void {
    if (this.visible?.kind === 'video') this.visible.video.currentTime = ms / 1000;
  }

  private hiddenSlot(): Slot {
    return this.visible === this.slots.A ? this.slots.B : this.slots.A;
  }

  private clearSlot(slot: Slot, emitHide: boolean): void {
    const wasVisible = this.visible === slot;
    try { slot.video.pause(); } catch { /* niente */ }
    slot.video.removeAttribute('src');
    slot.video.load();
    slot.video.playbackRate = 1;
    slot.img.removeAttribute('src');
    slot.video.style.display = 'none';
    slot.img.style.display = 'none';
    if (slot.url && slot.url.startsWith('blob:')) URL.revokeObjectURL(slot.url);
    slot.url = null;
    slot.kind = null;
    slot.root.style.zIndex = '1';
    if (wasVisible) this.visible = null;
    if (emitHide) this.events.push('hide', slot.id, null);
  }

  async prepare(spec: PrepareSpec): Promise<void> {
    this.discardPrepared();
    const seq = ++this.seq;
    try {
      await this.prepareInner(spec, seq);
    } catch (e) {
      // se nel frattempo è stata scartata (anche per un timeout causato dallo svuotamento dello slot) non è colpa dell'asset
      if (this.prepared?.seq !== seq) throw new SupersededError();
      throw e;
    }
  }

  private async prepareInner(spec: PrepareSpec, seq: number): Promise<void> {
    const slot = this.hiddenSlot();
    this.clearSlot(slot, false);
    this.prepared = { slot, spec, seq };
    slot.url = spec.url;
    slot.kind = spec.kind;
    const check = () => { if (this.prepared?.seq !== seq) throw new SupersededError(); };

    if (spec.kind === 'image') {
      slot.img.style.display = 'block';
      slot.img.src = spec.url;
      await slot.img.decode();
      check();
      return;
    }
    const v = slot.video;
    slot.video.style.display = 'block';
    v.muted = this.muted;
    v.preload = 'auto';
    v.playbackRate = 1;
    v.src = spec.url;
    v.load();
    if (v.readyState < 3) await waitEvent(v, ['canplaythrough', 'canplay'], 20_000, `video ${spec.assetId}`);
    check();
    if (spec.offsetMs > 0) {
      const seeked = waitEvent(v, ['seeked'], 5_000, `seek ${spec.assetId}`);
      v.currentTime = spec.offsetMs / 1000;
      await seeked;
      check();
    }
    v.pause();
  }

  discardPrepared(): void {
    if (!this.prepared) return;
    const { slot } = this.prepared;
    this.prepared = null;
    this.seq++;
    if (slot !== this.visible) this.clearSlot(slot, false);
  }

  private async startVideo(slot: Slot): Promise<void> {
    const v = slot.video as VideoWithRvfc;
    v.muted = this.muted;
    const playing = waitEvent(v, ['playing'], 5_000, 'playing');
    try {
      await v.play();
    } catch (e) {
      if (e instanceof DOMException && e.name === 'NotAllowedError' && !v.muted) {
        this.log('autoplay con audio rifiutato: riprovo muted');
        this.muted = true;
        this.autoplayFallback = true;
        v.muted = true;
        await v.play();
      } else {
        playing.catch(() => undefined);
        throw e;
      }
    }
    await playing;
    if (typeof v.requestVideoFrameCallback === 'function') {
      await new Promise<void>((resolve) => {
        const t = setTimeout(resolve, 500);
        v.requestVideoFrameCallback?.(() => { clearTimeout(t); resolve(); });
      });
    }
  }

  async commit(): Promise<void> {
    const p = this.prepared;
    if (!p) throw new Error('nessun elemento preparato');
    this.prepared = null; // da qui non è più "scartabile"
    const slot = p.slot;
    const old = this.visible;
    if (slot.kind === 'video') {
      try {
        await this.startVideo(slot);
      } catch (e) {
        this.clearSlot(slot, false);
        throw e;
      }
    }
    this.events.push('playing', slot.id, p.spec.scheduleId);
    // il nuovo sale sopra; il vecchio resta sotto, ancora intatto
    slot.root.style.zIndex = '2';
    if (old) old.root.style.zIndex = '1';
    this.visible = slot;
    this.events.push('show', slot.id, p.spec.scheduleId, p.spec.assetId, p.spec.durationMs ?? null);
    if (old) {
      await nextFrame();
      await nextFrame();
      this.clearSlot(old, true); // pausa + svuota SOLO dopo che il nuovo è a schermo
    }
  }

  /** Dimensioni dello schermo (stage) in px CSS, a prescindere dalla rotazione applicata con transform. */
  stageSize(): Size {
    return { width: this.stage.offsetWidth, height: this.stage.offsetHeight };
  }

  /** Lo slot visibile come sorgente per un canvas, o null se non c'è nulla (o il video non ha ancora un fotogramma). */
  snapshot(): Snapshot | null {
    const s = this.visible;
    if (!s) return null;
    const { width, height } = this.stageSize();
    if (s.kind === 'video' && s.video.videoWidth > 0 && s.video.readyState >= 2) {
      return { element: s.video, srcW: s.video.videoWidth, srcH: s.video.videoHeight, stageW: width, stageH: height };
    }
    if (s.kind === 'image' && s.img.naturalWidth > 0) {
      return { element: s.img, srcW: s.img.naturalWidth, srcH: s.img.naturalHeight, stageW: width, stageH: height };
    }
    return null;
  }

  showBlack(): void {
    this.prepared = null;
    this.seq++;
    for (const s of [this.slots.A, this.slots.B]) this.clearSlot(s, s === this.visible);
    this.visible = null;
  }
}
